import { useState, useEffect, useRef, useCallback } from "react";
import { RACE_COLORS, FONT_FAMILY, CT_LABELS, CT_OPTIONS } from "./constants.js";
import RaceDonut from "./components/RaceDonut.jsx";
import BarChartRace from "./components/BarChartRace.jsx";
import AgePyramid from "./components/AgePyramid.jsx";
import DisparityDot from "./components/DisparityDot.jsx";
import TimeSeries from "./components/TimeSeries.jsx";
import DisparityCards from "./components/DisparityCards.jsx";
import StopRateCards from "./components/StopRateCards.jsx";
import Methodology from "./components/Methodology.jsx";

// ---------------------------------------------------------------------------
// Scroll hint toast (mobile only, shown once via localStorage)
// ---------------------------------------------------------------------------

function SwipeHint() {
  const STORAGE_KEY = "cz_swipe_hint_dismissed";
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const alreadySeen = localStorage.getItem(STORAGE_KEY);
      if (!alreadySeen) {
        setVisible(true);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => dismiss(), 5000);
    return () => clearTimeout(timer);
  }, [visible]);

  function dismiss() {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
  }

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        bottom: 90,
        left: "50%",
        transform: "translateX(-50%)",
        background: "#0a0a0a",
        color: "#ffffff",
        fontFamily: FONT_FAMILY,
        fontSize: 15,
        fontWeight: 600,
        padding: "12px 22px",
        borderRadius: 28,
        zIndex: 99999,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      Swipe charts right to see more →
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getUrlParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    id:  p.get("id")  ? parseInt(p.get("id"), 10) : null,
    ct:  p.get("ct")  || null,
    nr:  p.get("nr")  || "0",
  };
}

function setUrlParams(id, ct, nr) {
  const p = new URLSearchParams();
  if (id != null) p.set("id", id);
  if (ct) p.set("ct", ct);
  p.set("nr", nr);
  window.history.pushState({}, "", `?${p.toString()}`);
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const appStyle = {
  fontFamily: FONT_FAMILY,
  maxWidth: 1400,
  width: "100%",
  margin: "0 auto",
  padding: "16px 16px 2.5rem",
  background: "#ffffff",
  color: "#0a0a0a",
  boxSizing: "border-box",
};

const headerStyle = {
  marginBottom: 24,
};

const h1Style = {
  fontSize: 28,
  fontWeight: 800,
  color: "#0a0a0a",
  margin: 0,
  fontFamily: FONT_FAMILY,
};

const searchContainerStyle = {
  position: "relative",
  marginBottom: 20,
  maxWidth: 480,
};

const searchInputStyle = {
  width: "100%",
  padding: "10px 14px",
  fontSize: 16,
  border: "1.5px solid #0a0a0a",
  borderRadius: 0,
  fontFamily: FONT_FAMILY,
  boxSizing: "border-box",
  outline: "none",
  background: "#ffffff",
};

const dropdownStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "white",
  border: "1.5px solid #0a0a0a",
  borderTop: "none",
  borderRadius: 0,
  maxHeight: 280,
  overflowY: "auto",
  zIndex: 100,
  boxShadow: "2px 2px 14px rgba(0,0,0,0.15)",
};

const dropdownItemStyle = (active) => ({
  padding: "9px 14px",
  cursor: "pointer",
  fontSize: 15,
  fontFamily: FONT_FAMILY,
  background: active ? "#79d6f9" : "white",
  color: "#0a0a0a",
  borderBottom: "1px solid #e8e8e8",
});

const sectionStyle = {
  marginTop: 32,
  marginBottom: 8,
};

const h2Style = {
  fontSize: 22,
  fontWeight: 700,
  color: "#0a0a0a",
  margin: "0 0 16px",
  fontFamily: FONT_FAMILY,
};

const dividerStyle = {
  borderTop: "2px solid #0a0a0a",
  margin: "32px 0 24px",
};

const cardStyle = {
  background: "#f2f2f2",
  border: "1px solid #ddd",
  borderRadius: 0,
  padding: "14px 18px",
  textAlign: "center",
  flex: "1 1 130px",
  minWidth: 0,
};

const cardLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.07em",
  color: "#777",
  textTransform: "uppercase",
  fontFamily: FONT_FAMILY,
  marginBottom: 4,
};

const cardValueStyle = (color) => ({
  fontSize: "clamp(18px, 4vw, 28px)",
  fontWeight: 800,
  color: color || "#222",
  fontFamily: FONT_FAMILY,
  lineHeight: 1.1,
});

const rowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const halfStyle = {
  flex: "1 1 320px",
  maxWidth: "100%",
  minWidth: 0,
};

const warningBannerStyle = {
  position: "sticky",
  top: 0,
  zIndex: 100,
  background: "#fff8e1",
  border: "1px solid #f9c74f",
  borderRadius: 8,
  padding: "14px 20px",
  marginBottom: 20,
  fontSize: 16,
  fontWeight: 600,
  color: "#7a5c00",
  fontFamily: FONT_FAMILY,
};

const ctToggleStyle = {
  display: "flex",
  gap: 0,
  marginBottom: 16,
  border: "1.5px solid #0a0a0a",
  borderRadius: 0,
  overflow: "hidden",
  width: "fit-content",
};

const ctBtnStyle = (active) => ({
  padding: "8px 18px",
  fontSize: 14,
  fontWeight: active ? 700 : 400,
  background: active ? "#0a0a0a" : "#ffffff",
  color: active ? "#ffffff" : "#0a0a0a",
  border: "none",
  cursor: "pointer",
  fontFamily: FONT_FAMILY,
  transition: "all 0.15s",
});

const nrCheckStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 20,
  fontSize: 16,
  color: "#444",
  fontFamily: FONT_FAMILY,
  cursor: "pointer",
};

// ---------------------------------------------------------------------------
// Color Key
// ---------------------------------------------------------------------------

function ColorKey() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 10, marginBottom: 4, fontSize: 16, fontWeight: 700, fontFamily: FONT_FAMILY }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 3, background: RACE_COLORS.BLACK, flexShrink: 0 }} />
        <span style={{ color: RACE_COLORS.BLACK }}>Black</span>
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 3, background: RACE_COLORS.WHITE, flexShrink: 0 }} />
        <span style={{ color: RACE_COLORS.WHITE }}>White</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metric Card
// ---------------------------------------------------------------------------

function MetricCard({ label, value, color, subtitle }) {
  return (
    <div style={cardStyle}>
      <div style={cardLabelStyle}>{label}</div>
      <div style={cardValueStyle(color)}>{value}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#888", marginTop: 4, lineHeight: 1.4 }}>{subtitle}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Disparity Headline
// ---------------------------------------------------------------------------

function DisparityHeadline({ meta, stopRateCards }) {
  if (!meta || !stopRateCards) return null;
  const { name, contact_type } = meta;
  const { black_rate, white_rate, disparity_ratio } = stopRateCards;

  if (disparity_ratio == null || !isFinite(disparity_ratio) || disparity_ratio === 0 || black_rate == null || white_rate == null) {
    return null;
  }

  const ctLabel = CT_LABELS[
    Object.entries({ all: "All", ped: "Pedestrian", mv: "Motor Vehicle" })
      .find(([_slug, ct]) => ct === contact_type)?.[0] || "all"
  ];

  const yearPrefix = meta.year_first === meta.year_last
    ? `In ${meta.year_first}*, `
    : `From ${meta.year_first} to ${meta.year_last}*, `;

  const noun = contact_type === "Pedestrian" ? "pedestrians"
    : contact_type === "Motor Vehicle" ? "motorists"
    : "residents";

  const isWhiteHigher = disparity_ratio < 1;
  const invertedRatio = (1 / disparity_ratio).toFixed(2);

  const headline = disparity_ratio >= 1
    ? `<span style="color:#1a1a2e">${yearPrefix}Officer ${name} stopped <span style="color:${RACE_COLORS.BLACK};font-weight:700">Black</span> ${noun} at <strong style="color:#1a1a2e">${disparity_ratio.toFixed(2)}x</strong> the rate of <span style="color:${RACE_COLORS.WHITE};font-weight:700">White</span> ${noun}.</span>`
    : `<span style="color:#1a1a2e">${yearPrefix}Officer ${name} stopped <span style="color:${RACE_COLORS.WHITE};font-weight:700">White</span> ${noun} at <strong style="color:#1a1a2e">${invertedRatio}x</strong> the rate of <span style="color:${RACE_COLORS.BLACK};font-weight:700">Black</span> ${noun}.</span>`;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        background: "#f2f2f2",
        borderLeft: "6px solid #0a0a0a",
        borderRadius: 0,
        padding: "20px 28px",
      }}
        dangerouslySetInnerHTML={{ __html: `<p style="font-size:clamp(16px,3vw,26px);margin:0;color:#1a1a2e;font-family:${FONT_FAMILY}">${headline}</p>` }}
      />
      <p style={{ fontSize: 13, fontStyle: "italic", color: "#666", margin: "6px 0 0", fontFamily: FONT_FAMILY }}>
        *Years active in CPD's 2009–2025 contact card data. "Active" refers to years with at least one stop on record, not total time served on the force. Officers may have additional years of service in roles that do not involve making stops.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top 10 Disparity Table
// ---------------------------------------------------------------------------

function Top10Table({ officerIndex, onSelect }) {
  const [filterMinStops, setFilterMinStops] = useState(true);
  const [filterActiveYears, setFilterActiveYears] = useState(true);
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  // When filterActiveYears is on: use 2020+ specific fields for stops, disparity, and year range.
  // When off: use all-years fields so the full dataset is ranked.
  const getStops     = o => filterActiveYears ? (o.total_stops_2020 ?? 0)          : (o.total_stops ?? 0);
  const getDisparity = o => filterActiveYears ? (o.disparity_ratio_2020 ?? null)   : (o.disparity_ratio ?? null);
  const getPctBlack  = o => filterActiveYears ? (o.pct_black_2020 ?? null)         : (o.pct_black ?? null);
  const getPctWhite  = o => filterActiveYears ? (o.pct_white_2020 ?? null)         : (o.pct_white ?? null);
  const getBlackRate = o => filterActiveYears ? (o.black_rate_2020 ?? null)        : (o.black_rate ?? null);
  const getWhiteRate = o => filterActiveYears ? (o.white_rate_2020 ?? null)        : (o.white_rate ?? null);
  const getYears     = o => filterActiveYears
    ? (o.year_first_2020 && o.year_last_2020 ? (o.year_first_2020 === o.year_last_2020 ? `${o.year_first_2020}` : `${o.year_first_2020}–${o.year_last_2020}`) : "—")
    : (o.year_first && o.year_last ? (o.year_first === o.year_last ? `${o.year_first}` : `${o.year_first}–${o.year_last}`) : "—");

  const rows = officerIndex
    .filter(o => !filterActiveYears || (
      o.year_first_2020 != null && o.year_last_2020 != null &&
      o.year_first_2020 >= 2020 && o.year_last_2020 <= 2025
    ))
    .filter(o => !filterMinStops || getStops(o) >= 100)
    .sort((a, b) => {
      const da = getDisparity(a), db = getDisparity(b);
      if (db == null && da == null) return 0;
      if (db == null) return -1;
      if (da == null) return 1;
      return db - da;
    });

  if (!officerIndex.length) return null;

  const thStyle = (align = "left", color = "#555") => ({
    textAlign: align,
    fontFamily: FONT_FAMILY,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color,
    padding: "7px 8px",
    borderBottom: "2px solid #0a0a0a",
    whiteSpace: "normal",
    lineHeight: 1.3,
    overflow: "hidden",
    verticalAlign: "bottom",
  });

  const tdStyle = (i, align = "left") => ({
    padding: "8px 8px",
    fontFamily: FONT_FAMILY,
    fontSize: 15,
    color: "#0a0a0a",
    borderBottom: "1px solid #e8e8e8",
    background: i % 2 === 0 ? "#ffffff" : "#f9f9f9",
    textAlign: align,
    whiteSpace: "nowrap",
  });

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0a0a0a", margin: "0 0 4px", fontFamily: FONT_FAMILY }}>
        Officers by Racial Disparity ({filterActiveYears ? "2020–2025" : "2009–2025"})
      </h2>
      <p style={{ fontFamily: FONT_FAMILY, fontSize: 15, margin: "0 0 12px" }}>
        Throughout this dashboard, <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>Purple</span> refers to <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>Black</span> residents and <span style={{ color: RACE_COLORS.WHITE, fontWeight: 700 }}>Pink</span> refers to <span style={{ color: RACE_COLORS.WHITE, fontWeight: 700 }}>White</span> residents. In the disparity ratios specifically, purple means Black residents were stopped at a higher rate; pink means White residents were stopped at a higher rate. Disparity ratios are calculated using beat level boundaries provided by Cincinnati Police.
      </p>
      <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
        <label style={nrCheckStyle}>
          <input
            type="checkbox"
            checked={filterMinStops}
            onChange={e => setFilterMinStops(e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer" }}
          />
          Minimum 100 stops
        </label>
        <label style={nrCheckStyle}>
          <input
            type="checkbox"
            checked={filterActiveYears}
            onChange={e => setFilterActiveYears(e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer" }}
          />
          Show 2020–2025 data only
        </label>
      </div>
      {rows.length === 0 && (
        <p style={{ fontFamily: FONT_FAMILY, fontSize: 14, color: "#888" }}>
          No officers match the current filters.
        </p>
      )}
      <p style={{ fontFamily: FONT_FAMILY, fontSize: 14, color: "#555", margin: "0 0 6px" }}>
        Sorted by disparity ratio, highest to lowest. Click on any row to view stop data for that officer.
      </p>
      {rows.length > 0 && <div style={{ maxHeight: 460, overflowY: "auto", overflowX: "auto", border: "1px solid #ddd" }}>
        <table style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "#ffffff" }}>
          <tr>
            <th style={{ ...thStyle(), width: "4%" }}>#</th>
            <th style={{ ...thStyle(), width: "14%" }}>Officer</th>
            <th style={{ ...thStyle(), width: "8%" }}>Personnel ID</th>
            <th style={{ ...thStyle(), width: "9%" }}>Years Analyzed</th>
            <th style={{ ...thStyle("right"), width: "7%" }}>Total Stops</th>
            <th style={{ ...thStyle("right", RACE_COLORS.BLACK), width: "9%" }}>% Black Stops</th>
            <th style={{ ...thStyle("right", RACE_COLORS.WHITE), width: "9%" }}>% White Stops</th>
            <th style={{ ...thStyle("right", RACE_COLORS.BLACK), width: "11%" }}>Black Rate<br />/1K/yr</th>
            <th style={{ ...thStyle("right", RACE_COLORS.WHITE), width: "11%" }}>White Rate<br />/1K/yr</th>
            <th style={{ ...thStyle("right"), width: "11%" }}>Disparity<br />Ratio</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o, i) => (
            <tr
              key={o.pid}
              onClick={() => onSelect(o)}
              style={{ cursor: "pointer" }}
              onMouseEnter={e => Array.from(e.currentTarget.cells).forEach(td => { td.style.background = "#e8f7fd"; })}
              onMouseLeave={e => Array.from(e.currentTarget.cells).forEach(td => { td.style.background = i % 2 === 0 ? "#ffffff" : "#f9f9f9"; })}
            >
              <td style={{ ...tdStyle(i), color: "#aaa", fontWeight: 700, width: "5%" }}>{i + 1}</td>
              <td style={{ ...tdStyle(i), fontWeight: 600, whiteSpace: "normal", wordBreak: "break-word" }}>{o.name}</td>
              <td style={{ ...tdStyle(i), color: "#555" }}>{o.pid}</td>
              <td style={{ ...tdStyle(i), color: "#555" }}>{getYears(o)}</td>
              <td style={{ ...tdStyle(i, "right"), color: "#555" }}>{getStops(o).toLocaleString()}</td>
              <td style={{ ...tdStyle(i, "right"), color: RACE_COLORS.BLACK, fontWeight: 600 }}>
                {getPctBlack(o) != null ? `${getPctBlack(o)}%` : "—"}
              </td>
              <td style={{ ...tdStyle(i, "right"), color: RACE_COLORS.WHITE, fontWeight: 600 }}>
                {getPctWhite(o) != null ? `${getPctWhite(o)}%` : "—"}
              </td>
              <td style={{ ...tdStyle(i, "right"), color: RACE_COLORS.BLACK }}>
                {getBlackRate(o) != null ? getBlackRate(o).toFixed(2) : "—"}
              </td>
              <td style={{ ...tdStyle(i, "right"), color: RACE_COLORS.WHITE }}>
                {getWhiteRate(o) != null ? getWhiteRate(o).toFixed(2) : "—"}
              </td>
              <td style={{ ...tdStyle(i, "right"), fontWeight: 700, color: getDisparity(o) == null ? "#888" : getDisparity(o) > 1 ? RACE_COLORS.BLACK : getDisparity(o) < 1 ? RACE_COLORS.WHITE : "#888" }}>
                {getDisparity(o) != null && isFinite(getDisparity(o)) && getDisparity(o) !== 0 ? `${getDisparity(o) < 1 ? (1 / getDisparity(o)).toFixed(2) : getDisparity(o).toFixed(2)}x` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>}
      <p style={{ fontFamily: FONT_FAMILY, fontSize: 14, color: "#555", margin: "10px 0 0" }}>
        <strong>Disparity ratios</strong> are shown in the table in either <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>purple</span> for disparities where <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>Black residents were stopped at higher rates</span> or <span style={{ color: RACE_COLORS.WHITE, fontWeight: 700 }}>pink</span> for disparities where <span style={{ color: RACE_COLORS.WHITE, fontWeight: 700 }}>White residents were stopped at higher rates</span>.
      </p>
      {/* Filter notes */}
      <div style={{ marginTop: 16, borderTop: "1px solid #e8e8e8", paddingTop: 12 }}>
        <p style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: "#666", margin: "0 0 6px", lineHeight: 1.6 }}>
          <span style={{ fontWeight: 700, color: "#0a0a0a" }}>Minimum 100 stops</span> is selected by default to ensure rates and disparity ratios are statistically meaningful. Officers with fewer stops may not have enough data to be representative. A "—" in any column means the value could not be calculated, either because the officer had no stops of one or both racial groups, or because the underlying data was insufficient to compute a meaningful rate or ratio.
        </p>
        <p style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: "#666", margin: "0 0 6px", lineHeight: 1.6 }}>
          <span style={{ fontWeight: 700, color: "#0a0a0a" }}>Show 2020–2025 data only</span> is selected by default because officers active in the last six years are more likely to still be serving in the Cincinnati Police Department. When this filter is on, all statistics — stop counts, rates, and disparity ratios — are calculated using only stops made from 2020 onwards. Unchecking it includes the full 2009–2025 dataset. Click on any officer's name to view their detailed stop statistics for the selected period.
        </p>
        <p style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: "#666", margin: 0, lineHeight: 1.6 }}>
          Only stops made within Cincinnati city limits are included. Contact card records from outside the city but within Hamilton County are excluded. The Cincinnati contact card dataset contains 472,666 stops made by 1,545 officers between 2009 and 2025. Of those, 49,005 stops are interstate highway stops and are excluded because highway stops cannot be benchmarked against a residential population — interstates pass through but are not part of any residential neighborhood. Of the 1,545 officers, 14 are excluded from this dashboard: 13 made stops exclusively on interstates, and 1 is a system account. This dashboard covers 423,661 stops made by 1,531 officers.
        </p>
      </div>

      {/* Collapsible methodology */}
      <div style={{ marginTop: 24, borderTop: "2px solid #0a0a0a", paddingTop: 16 }}>
        <button
          onClick={() => setMethodologyOpen(o => !o)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: FONT_FAMILY,
            fontSize: 18,
            fontWeight: 700,
            color: "#0a0a0a",
            marginBottom: methodologyOpen ? 14 : 0,
          }}
          aria-expanded={methodologyOpen}
        >
          <span style={{ fontSize: 13, color: "#888" }}>{methodologyOpen ? "▲" : "▼"}</span>
          Notes &amp; Methodology
        </button>

        {methodologyOpen && (
          <div style={{ fontFamily: FONT_FAMILY, color: "#333", lineHeight: 1.7, fontSize: 14, overflowX: "auto" }}>

            {/* Step 1 */}
            <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Step 1: Calculate the weighted population</p>
            <p style={{ marginBottom: 14 }}>
              Beat-level population estimates are derived from ACS data at the census tract level. Since census tracts do not align perfectly with CPD beats, we use weighted areal interpolation: for each tract, we calculate what share of its area falls within each beat, then allocate that proportion of the tract's population to the beat. This is done separately for each ACS year (2009–2025), producing a beat × year population estimate that tracks demographic changes over time.
            </p>
            <p style={{ marginBottom: 14 }}>
              Census tract boundaries are redrawn every decade following the decennial census. To ensure the population estimates match the geography that was actually in use at the time of each stop, we use the appropriate tract boundary vintage for each year: 2000 boundaries for 2009, 2010 boundaries for 2010–2019, and 2020 boundaries for 2020–2025. Using a single boundary vintage for all years would introduce a mismatch between the population geography and the stop year, which would distort the weighted population estimates — particularly for officers with long careers spanning multiple census decades.
            </p>
            <p style={{ marginBottom: 14 }}>
              For each officer, we identify every beat in which they made stops recorded in the contact card dataset and in what years, then calculate what share of their stops occurred in each beat × year combination. Those shares are used to weight the ACS population for each beat and year, producing a single population figure tailored to that officer's actual stop history.
            </p>
            <div style={{ marginBottom: 16, fontStyle: "italic", textAlign: "center", background: "#f9f9f9", padding: "10px 16px", borderRadius: 4, lineHeight: 2 }}>
              <div>weight(beat, year) = [stops in (beat, year)] / [total stops]</div>
              <div>Weighted Population = Σ [ weight(beat, year) × ACS population(beat, year) ]</div>
            </div>

            <p style={{ marginBottom: 10, fontWeight: 600 }}>Example: Officer with 30 stops over 3 years (2020–2022) across 2 beats</p>
            <div style={{ overflowX: "auto", marginBottom: 14 }}><table style={{ width: "auto", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  <th style={{ textAlign: "left", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>Year</th>
                  <th style={{ textAlign: "right", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>Beat A stops</th>
                  <th style={{ textAlign: "right", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>Beat B stops</th>
                  <th style={{ textAlign: "right", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {[["2020",5,2,7],["2021",8,4,12],["2022",5,6,11]].map(([yr,a,b,t]) => (
                  <tr key={yr}>
                    <td style={{ padding: "5px 14px", borderBottom: "1px solid #eee" }}>{yr}</td>
                    <td style={{ textAlign: "right", padding: "5px 14px", borderBottom: "1px solid #eee" }}>{a}</td>
                    <td style={{ textAlign: "right", padding: "5px 14px", borderBottom: "1px solid #eee" }}>{b}</td>
                    <td style={{ textAlign: "right", padding: "5px 14px", borderBottom: "1px solid #eee" }}>{t}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 600, background: "#f5f5f5" }}>
                  <td style={{ padding: "5px 14px" }}>Total</td>
                  <td style={{ textAlign: "right", padding: "5px 14px" }}>18 (60%)</td>
                  <td style={{ textAlign: "right", padding: "5px 14px" }}>12 (40%)</td>
                  <td style={{ textAlign: "right", padding: "5px 14px" }}>30</td>
                </tr>
              </tbody>
            </table></div>

            <p style={{ marginBottom: 6 }}>Each beat × year is assigned a share of the officer's total stops. The ACS provides separate Black and White population estimates for each beat and year:</p>
            <div style={{ overflowX: "auto", marginBottom: 14 }}><table style={{ width: "auto", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  <th style={{ textAlign: "left", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>Beat × Year</th>
                  <th style={{ textAlign: "right", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>Share of stops</th>
                  <th style={{ textAlign: "right", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap", color: "#5E4FA2" }}>Black pop (ACS)</th>
                  <th style={{ textAlign: "right", padding: "6px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap", color: "#9D0142" }}>White pop (ACS)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Beat A, 2020","5/30 = 16.7%","1,000","500",false],
                  ["Beat B, 2020","2/30 = 6.7%","300","2,000",true],
                  ["Beat A, 2021","8/30 = 26.7%","1,020","510",false],
                  ["Beat B, 2021","4/30 = 13.3%","310","2,020",true],
                  ["Beat A, 2022","5/30 = 16.7%","1,040","520",false],
                  ["Beat B, 2022","6/30 = 20.0%","320","2,040",true],
                ].map(([by,sh,bp,wp,isGroupEnd]) => (
                  <tr key={by}>
                    <td style={{ padding: "5px 14px", borderBottom: isGroupEnd ? "2px solid #bbb" : "1px solid #eee" }}>{by}</td>
                    <td style={{ textAlign: "right", padding: "5px 14px", borderBottom: isGroupEnd ? "2px solid #bbb" : "1px solid #eee" }}>{sh}</td>
                    <td style={{ textAlign: "right", padding: "5px 14px", borderBottom: isGroupEnd ? "2px solid #bbb" : "1px solid #eee", color: "#5E4FA2" }}>{bp}</td>
                    <td style={{ textAlign: "right", padding: "5px 14px", borderBottom: isGroupEnd ? "2px solid #bbb" : "1px solid #eee", color: "#9D0142" }}>{wp}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>

            <p style={{ marginBottom: 4, fontStyle: "italic", paddingLeft: 20, borderLeft: "3px solid #5E4FA2", color: "#5E4FA2" }}>
              Weighted Black pop = (0.167×1,000) + (0.067×300) + (0.267×1,020) + (0.133×310) + (0.167×1,040) + (0.200×320) = <strong>737</strong>
            </p>
            <p style={{ marginBottom: 24, fontStyle: "italic", paddingLeft: 20, borderLeft: "3px solid #9D0142", color: "#9D0142" }}>
              Weighted White pop = (0.167×500) + (0.067×2,000) + (0.267×510) + (0.133×2,020) + (0.167×520) + (0.200×2,040) = <strong>1,117</strong>
            </p>

            {/* Step 2 */}
            <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Step 2: Calculate stop rates</p>
            <p style={{ marginBottom: 14 }}>
              Using the weighted population from Step 1, stop rates express how many people of each race the officer stopped per 1,000 residents per year, based on where and when they actually made stops.
            </p>
            <p style={{ marginBottom: 16, fontStyle: "italic", textAlign: "center", background: "#f9f9f9", padding: "10px 16px", borderRadius: 4 }}>
              Stop Rate = (# of stops for race group ÷ years active) ÷ (weighted population for race group ÷ 1,000)
            </p>
            <p style={{ marginBottom: 14 }}>
              Stop rates are expressed per 1,000 residents rather than as raw proportions because it makes the numbers easier to interpret. For a city the size of Cincinnati, per 1,000 is the standard scale — it avoids very small decimals while keeping the numbers intuitive. Larger jurisdictions often use per 100,000 or even per million for national-level indicators, but at the city level, per 1,000 strikes the right balance between readability and precision.
            </p>
            <p style={{ marginBottom: 6 }}>If this officer stopped <span style={{ color: "#5E4FA2" }}><strong>20</strong> Black people</span> and <span style={{ color: "#9D0142" }}><strong>4</strong> White people</span> over 3 years:</p>
            <p style={{ marginBottom: 4, fontStyle: "italic", paddingLeft: 20, borderLeft: "3px solid #5E4FA2", color: "#5E4FA2" }}>Black stop rate = (<strong>20</strong> ÷ 3) ÷ (<strong>737</strong> ÷ 1,000) = 6.67 ÷ 0.737 = <strong>9.05 per 1,000 Black residents per year</strong></p>
            <p style={{ marginBottom: 24, fontStyle: "italic", paddingLeft: 20, borderLeft: "3px solid #9D0142", color: "#9D0142" }}>White stop rate = (<strong>4</strong> ÷ 3) ÷ (<strong>1,117</strong> ÷ 1,000) = 1.33 ÷ 1.117 = <strong>1.19 per 1,000 White residents per year</strong></p>

            {/* Step 3 */}
            <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Step 3: Calculate the disparity ratio</p>
            <p style={{ marginBottom: 14 }}>
              Compares stop rates between Black and White residents. A value of 1.00x means both groups were stopped at the same rate relative to their population. Disparities shown in <span style={{ color: "#5E4FA2", fontWeight: 600 }}>purple</span> mean Black residents were stopped at a higher rate. Disparities shown in <span style={{ color: "#9D0142", fontWeight: 600 }}>pink</span> mean White residents were stopped at a higher rate.
            </p>
            <p style={{ marginBottom: 16, fontStyle: "italic", textAlign: "center", background: "#f9f9f9", padding: "10px 16px", borderRadius: 4 }}>
              Disparity Ratio = <span style={{ color: "#5E4FA2", fontWeight: 600 }}>Black Stop Rate</span> ÷ <span style={{ color: "#9D0142", fontWeight: 600 }}>White Stop Rate</span>
            </p>
            <p style={{ marginBottom: 8, fontStyle: "italic", paddingLeft: 20, borderLeft: "3px solid #ddd" }}>Disparity ratio = <span style={{ color: "#5E4FA2", fontWeight: 600 }}>9.05</span> ÷ <span style={{ color: "#9D0142", fontWeight: 600 }}>1.19</span> = <span style={{ color: "#5E4FA2", fontWeight: 700 }}>7.60x</span></p>
            <p style={{ marginBottom: 24, paddingLeft: 20, color: "#444" }}><strong>Interpretation:</strong> From 2020 to 2022, this officer stopped <span style={{ color: "#5E4FA2", fontWeight: 600 }}>Black</span> residents at <span style={{ color: "#5E4FA2", fontWeight: 700 }}>7.60x</span> the rate of <span style={{ color: "#9D0142", fontWeight: 600 }}>White</span> residents.</p>

            <p style={{ marginBottom: 0 }}>
              Figures based on fewer than 30 stops should be interpreted with caution. Small counts can produce unstable rates and ratios that may not reflect consistent patterns.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible Methodology
// ---------------------------------------------------------------------------

function CollapsibleMethodology() {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: FONT_FAMILY,
          fontSize: 22,
          fontWeight: 700,
          color: "#0a0a0a",
          marginBottom: open ? 16 : 0,
        }}
        aria-expanded={open}
      >
        <span style={{ fontSize: 14, color: "#888" }}>{open ? "▲" : "▼"}</span>
        Notes &amp; Methodology
      </button>
      {open && <Methodology />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------

export default function App() {
  const [officerIndex, setOfficerIndex] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeDropdownIdx, setActiveDropdownIdx] = useState(-1);
  const [selectedOfficer, setSelectedOfficer] = useState(null); // index entry
  const [activeCt, setActiveCt] = useState("all");
  const [showNr, setShowNr] = useState(false);
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  const dropdownRef = useRef();
  const appRef = useRef();

  // Load officer index — always start at table view, clear any stale URL params
  useEffect(() => {
    window.history.replaceState({}, "", window.location.pathname);
    fetch("/data/officers_index.json")
      .then(r => r.json())
      .then(data => setOfficerIndex(data))
      .catch(err => console.error("Failed to load officer index:", err));
  }, []);

  // Fetch dashboard data when selection changes
  useEffect(() => {
    if (!selectedOfficer) return;
    const nr = showNr ? "1" : "0";
    const url = `/data/officers/${selectedOfficer.pid}_${activeCt}_${nr}.json`;
    setLoading(true);
    setDashData(null);
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setDashData(data);
        setLoading(false);
        setUrlParams(selectedOfficer.pid, activeCt, nr);
      })
      .catch(err => {
        console.error("Failed to load officer data:", err);
        setLoading(false);
      });
  }, [selectedOfficer, activeCt, showNr]);

  // Filtered officer list for dropdown
  const sortedIndex = [...officerIndex].sort((a, b) => a.name.localeCompare(b.name));
  const filtered = searchText.length >= 1
    ? officerIndex
        .filter(o =>
          o.name.toLowerCase().includes(searchText.toLowerCase()) ||
          String(o.pid).includes(searchText.trim())
        )
        .slice(0, 12)
    : sortedIndex;

  function selectOfficer(officer) {
    setSelectedOfficer(officer);
    setSearchText(officer.name);
    setShowDropdown(false);
    setActiveDropdownIdx(-1);
    const firstCt = officer.contact_types[0] || "all";
    setActiveCt(firstCt);
    setShowNr(false);
  }

  function handleKeyDown(e) {
    if (!showDropdown || !filtered.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveDropdownIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveDropdownIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeDropdownIdx >= 0) {
        selectOfficer(filtered[activeDropdownIdx]);
      } else if (filtered.length === 1) {
        selectOfficer(filtered[0]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  }

  const meta = dashData?.meta;
  const src = dashData?.stop_rate_cards;

  // Overview metric rows
  const metricRow1 = meta ? [
    { label: "Total Stops", value: meta.total_stops?.toLocaleString() },
    { label: "Years Active", value: meta.year_first === meta.year_last ? `${meta.year_first}` : `${meta.year_first}–${meta.year_last}` },
    { label: "Stops / Year", value: meta.stops_per_year != null ? meta.stops_per_year.toFixed(1) : "—" },
    { label: "Disparity Ratio", value: src?.disparity_ratio != null && isFinite(src.disparity_ratio) && src.disparity_ratio !== 0 ? `${src.disparity_ratio < 1 ? (1 / src.disparity_ratio).toFixed(2) : src.disparity_ratio.toFixed(2)}x` : "—", color: src?.disparity_ratio != null && src.disparity_ratio < 1 ? RACE_COLORS.WHITE : RACE_COLORS.BLACK },
  ] : [];

  const metricRow2 = meta ? [
    { label: "% Black Stops", value: meta.pct_black != null ? `${meta.pct_black}%` : "—", color: RACE_COLORS.BLACK },
    { label: "% White Stops", value: meta.pct_white != null ? `${meta.pct_white}%` : "—", color: RACE_COLORS.WHITE },
    { label: "Black Rate / 1K / Yr", value: src?.black_rate != null ? src.black_rate.toFixed(2) : "—", color: RACE_COLORS.BLACK },
    { label: "White Rate / 1K / Yr", value: src?.white_rate != null ? src.white_rate.toFixed(2) : "—", color: RACE_COLORS.WHITE },
  ] : [];

  const availableCts = selectedOfficer ? CT_OPTIONS.filter(o => selectedOfficer.contact_types.includes(o.slug)) : [];

  // Check if sections have data
  const hasGender = dashData?.gender?.length > 0;
  const hasAge = dashData?.age_pyramid?.length > 0;
  const hasStopReason = dashData?.stop_reason?.bars?.length > 0;
  const hasStopOutcome = dashData?.stop_outcome?.bars?.length > 0;
  const hasSearch = dashData?.search_legal_basis?.bars?.length > 0;
  const hasContraband = dashData?.contraband?.bars?.length > 0;
  const hasTimeSeries = dashData?.time_series?.some(d => d.total > 0);
  const hasDonut = dashData?.donut?.length > 0;

  const hasPeopleSection = hasDonut || hasGender || hasAge;
  const hasPatternsSection = hasStopReason || hasStopOutcome || hasSearch || hasContraband;

  return (
    <div ref={appRef} style={appStyle}>
      {selectedOfficer && <SwipeHint />}
      {/* Header */}
      <div style={headerStyle}>
        {/* Search */}
        <div style={searchContainerStyle}>
          <input
            ref={inputRef}
            style={searchInputStyle}
            type="text"
            placeholder="Search by name or personnel number"
            value={searchText}
            onChange={e => {
              setSearchText(e.target.value);
              setShowDropdown(true);
              setActiveDropdownIdx(-1);
              if (!e.target.value) {
                setSelectedOfficer(null);
                setDashData(null);
              }
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            onKeyDown={handleKeyDown}
            aria-label="Search officers"
            aria-autocomplete="list"
            aria-expanded={showDropdown && filtered.length > 0}
          />
          {showDropdown && filtered.length > 0 && (
            <div ref={dropdownRef} style={dropdownStyle} role="listbox">
              {filtered.map((o, i) => (
                <div
                  key={o.pid}
                  role="option"
                  aria-selected={i === activeDropdownIdx}
                  style={dropdownItemStyle(i === activeDropdownIdx)}
                  onMouseDown={() => selectOfficer(o)}
                  onMouseEnter={() => setActiveDropdownIdx(i)}
                >
                  <span style={{ fontWeight: 600 }}>{o.name}</span>
                  <span style={{ color: "#888", fontSize: 13, marginLeft: 6 }}>— {o.pid}</span>
                  <span style={{ color: "#bbb", fontSize: 13, marginLeft: 8 }}>
                    {o.total_stops.toLocaleString()} {o.total_stops === 1 ? "stop" : "stops"}
                    {o.disparity_ratio != null && (
                      <span style={{ color: o.disparity_ratio < 1 ? "#c97a96" : "#9b87c9", marginLeft: 0 }}>
                        {isFinite(o.disparity_ratio) && o.disparity_ratio !== 0 ? ` · ${o.disparity_ratio < 1 ? (1 / o.disparity_ratio).toFixed(2) : o.disparity_ratio.toFixed(2)}x disparity` : ""}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      {selectedOfficer && (
        <div>
          {/* Contact type toggle */}
          {availableCts.length > 1 && (
            <div style={ctToggleStyle} role="group" aria-label="Contact type">
              {availableCts.map(opt => (
                <button
                  key={opt.slug}
                  style={ctBtnStyle(activeCt === opt.slug)}
                  onClick={() => setActiveCt(opt.slug)}
                  aria-pressed={activeCt === opt.slug}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Show NOT RECORDED */}
          <label style={nrCheckStyle}>
            <input
              type="checkbox"
              checked={showNr}
              onChange={e => setShowNr(e.target.checked)}
              style={{ width: 16, height: 16, cursor: "pointer" }}
            />
            Include stops where data was not recorded
          </label>
        </div>
      )}

      {/* Top 10 table — shown before an officer is selected */}
      {!selectedOfficer && officerIndex.length > 0 && (
        <Top10Table officerIndex={officerIndex} onSelect={selectOfficer} />
      )}

      {/* Loading */}
      {loading && (
        <p style={{ color: "#888", fontFamily: FONT_FAMILY, fontSize: 15 }}>Loading...</p>
      )}

      {/* Dashboard */}
      {dashData && meta && (
        <div>
          {/* Return to table */}
          <button
            onClick={() => {
              setSelectedOfficer(null);
              setSearchText("");
              setDashData(null);
              appRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
              gap: 6,
              background: "none",
              border: "1.5px solid #0a0a0a",
              padding: "6px 14px",
              fontFamily: FONT_FAMILY,
              fontSize: 14,
              fontWeight: 600,
              color: "#0a0a0a",
              cursor: "pointer",
              marginBottom: 20,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            ← Back to Officers by Racial Disparity table
          </button>

          {/* Low data warning */}
          {meta.total_stops < 30 && (
            <div style={warningBannerStyle}>
              ⚠️ Limited data. This officer has fewer than 30 stops on record ({meta.total_stops} {meta.total_stops === 1 ? "stop" : "stops"} in total). Some statistics may not be computable or reliable. Interpret with caution.
            </div>
          )}

          {/* Disparity headline */}
          <DisparityHeadline meta={meta} stopRateCards={src} />

          <p style={{ fontFamily: FONT_FAMILY, fontSize: 15, margin: "8px 0 16px" }}>
            Throughout this dashboard, <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>Purple</span> refers to <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>Black</span> residents and <span style={{ color: RACE_COLORS.WHITE, fontWeight: 700 }}>Pink</span> refers to <span style={{ color: RACE_COLORS.WHITE, fontWeight: 700 }}>White</span> residents. In the disparity ratios specifically, purple means Black residents were stopped at a higher rate; pink means White residents were stopped at a higher rate. Disparity ratios are calculated using beat level boundaries provided by Cincinnati Police.
          </p>

          {/* Overview metric cards */}
          {metricRow1.length > 0 && (
            <>
              <h2 style={{ ...h2Style, marginBottom: 12 }}>Overview</h2>
              <div style={{ ...rowStyle, marginBottom: 10 }}>
                {metricRow1.map(m => (
                  <MetricCard key={m.label} label={m.label} value={m.value} color={m.color} />
                ))}
              </div>
              <div style={rowStyle}>
                {metricRow2.map(m => (
                  <MetricCard key={m.label} label={m.label} value={m.value} color={m.color} />
                ))}
              </div>
            </>
          )}

          {/* People Stopped */}
          {hasPeopleSection && (
            <>
              <div style={dividerStyle} />
              <div style={sectionStyle}>
                <h2 style={h2Style}>People Stopped — {CT_LABELS[activeCt]}</h2>

                {/* Row 1: Donut + Gender */}
                {(hasDonut || hasGender) && (
                  <div style={rowStyle}>
                    {hasDonut && (
                      <div style={halfStyle}>
                        <RaceDonut data={dashData.donut} />
                        <ColorKey />
                      </div>
                    )}
                    {hasGender && (
                      <div style={halfStyle}>
                        <BarChartRace
                          data={dashData.gender}
                          title="Gender"
                          subtitle="What is the gender breakdown of people this officer stops, by race?"
                          sortOrder={["MALE", "FEMALE", "NOT RECORDED"]}
                        />
                        <ColorKey />
                      </div>
                    )}
                  </div>
                )}

                {/* Row 2: Age Pyramid */}
                {hasAge && (
                  <div style={{ ...rowStyle, marginTop: 16 }}>
                    <div style={halfStyle}>
                      <AgePyramid data={dashData.age_pyramid} />
                      <ColorKey />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Stop Patterns */}
          {hasPatternsSection && (
            <>
              <div style={dividerStyle} />
              <div style={sectionStyle}>
                <h2 style={h2Style}>Stop Patterns — {CT_LABELS[activeCt]}</h2>

                {/* Stop Reason */}
                {hasStopReason && (
                  <div style={{ ...rowStyle, marginBottom: 16 }}>
                    <div style={halfStyle}>
                      <BarChartRace
                        data={dashData.stop_reason.bars}
                        title="Initial Stop Reason"
                        subtitle="What reasons does this officer give for initiating stops, by race?"
                        sortOrder={dashData.stop_reason.sort_order}
                      />
                      <ColorKey />
                    </div>
                    <div style={halfStyle}>
                      <DisparityDot
                        data={dashData.stop_reason.disparity}
                        title="Racial Disparity in Stop Reason"
                        subtitle="Does this officer give different reasons for stopping Black vs. White residents?"
                        sortOrder={dashData.stop_reason.sort_order}
                      />
                    </div>
                  </div>
                )}

                {/* Stop Outcome */}
                {hasStopOutcome && (
                  <>
                    <p style={{ fontSize: 22, fontWeight: 700, color: "#0a0a0a", fontFamily: FONT_FAMILY, marginTop: 16, marginBottom: 4 }}>Stop Outcome</p>
                    <div style={{ ...rowStyle, marginBottom: 16 }}>
                      <div style={halfStyle}>
                        <BarChartRace
                          data={dashData.stop_outcome.bars}
                          title="Share of Stops by Outcome"
                          subtitle="What share of stops end in each outcome, by race?"
                          sortOrder={dashData.stop_outcome.sort_order}
                        />
                        <ColorKey />
                      </div>
                      <div style={halfStyle}>
                        <DisparityDot
                          data={dashData.stop_outcome.disparity}
                          title="Racial Disparity in Outcomes"
                          subtitle="After making a stop, does this officer apply different outcomes by race?"
                          sortOrder={dashData.stop_outcome.sort_order}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Search Legal Basis */}
                {hasSearch && (
                  <>
                    <p style={{ fontSize: 22, fontWeight: 700, color: "#0a0a0a", fontFamily: FONT_FAMILY, marginTop: 16, marginBottom: 4 }}>Searches</p>
                    <div style={{ ...rowStyle, marginBottom: 16 }}>
                      <div style={halfStyle}>
                        <BarChartRace
                          data={dashData.search_legal_basis.bars}
                          title="Share of Stops by Search Legal Basis"
                          subtitle="What legal basis does this officer use to conduct searches, by race?"
                          sortOrder={dashData.search_legal_basis.sort_order}
                        />
                        <ColorKey />
                      </div>
                      <div style={halfStyle}>
                        <DisparityDot
                          data={dashData.search_legal_basis.disparity}
                          title="Racial Disparity in Search Legal Basis"
                          subtitle="After making a stop, does this officer use different legal bases to conduct searches, by race?"
                          sortOrder={dashData.search_legal_basis.sort_order}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Contraband */}
                {hasContraband && (
                  <>
                    <p style={{ fontSize: 22, fontWeight: 700, color: "#0a0a0a", fontFamily: FONT_FAMILY, marginTop: 16, marginBottom: 4 }}>Contraband</p>
                    <div style={{ ...rowStyle, marginBottom: 16 }}>
                      <div style={halfStyle}>
                        <BarChartRace
                          data={dashData.contraband.bars}
                          title="Share of Stops by Contraband Found"
                          subtitle="How often does this officer find contraband, by race?"
                          sortOrder={dashData.contraband.sort_order}
                        />
                        <ColorKey />
                      </div>
                      <div style={halfStyle}>
                        <DisparityDot
                          data={dashData.contraband.disparity}
                          title="Racial Disparity in Contraband Found"
                          subtitle="After making a stop, does this officer find contraband at different rates, by race?"
                          sortOrder={dashData.contraband.sort_order}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Disparity Cards */}
                {dashData.disparity_cards && (
                  <DisparityCards data={dashData.disparity_cards} />
                )}
              </div>
            </>
          )}

          {/* Stops Over Time */}
          {hasTimeSeries && (
            <>
              <div style={dividerStyle} />
              <div style={sectionStyle}>
                <h2 style={h2Style}>Stops Over Time — {CT_LABELS[activeCt]}</h2>
                <TimeSeries data={dashData.time_series} />
              </div>
            </>
          )}

          {/* Notes & Methodology */}
          <div style={dividerStyle} />
          <div style={sectionStyle}>
            <CollapsibleMethodology />
          </div>
        </div>
      )}
    </div>
  );
}
